<template>
    <div class="login-modal-container" v-if="isModalOpen">
        <div class="grid-x login-modal">
            <div class="login-modal-close-button show-for-large">
                <a @click="closeLoginModal"><span v-html="Icon.MODAL_CLOSE"></span></a>
            </div>
            <div class="cell" v-if="currentStep === LoginStep.INIT">
                <h3 class="display-4 bold login-modal-step-title">Sign in or register</h3>
                <div>
                    <social-login-button provider="google"></social-login-button>
                    <social-login-button provider="facebook"></social-login-button>
                    <p class="color-alert" v-if="authError">{{ authError }}</p>
                    <hr class="line-divider" data-text="OR"/>
                    <text-input
                        label="Email"
                        type="email"
                        name="email"
                        :value="userEmail"
                        :error-message="emailError"
                        @change="handleInput('email', $event)"
                    ></text-input>
                    <button class="button full-width" @click="checkUserExists" dusk="login-continue-btn">Continue</button>
                </div>
                <p class="text-center display-small-tiny color-battleship-gray">By proceeding I accept {{ storeName }}’s <router-link to="/terms-conditions">Terms and Conditions</router-link> and <router-link to="/privacy-policy">Privacy Policy</router-link></p>
            </div>
            <div class="cell" v-if="currentStep === LoginStep.USER_EXISTS">
                <p class="login-modal-step-title display-4 bold">Welcome back, enter your password</p>
                <text-input
                    label="Password"
                    type="password"
                    name="password"
                    :value="userPassword"
                    @change="handleInput('password', $event)"
                    :error-message="wrongPasswordText"
                ></text-input>
                <button class="button full-width" @click="login" dusk="login-button">Log in</button>
                <p class="text-center display-small-medium"><a @click="goToPasswordLost" dusk="login-forgot-password">Forgot your password?</a></p>
            </div>
            <div class="cell" v-if="currentStep === LoginStep.PASSWORD_RESET">
                <p class="login-modal-step-title display-4 bold">Reset your password</p>
                <div v-if="resetError">
                    <p v-if="resetError === PasswordResetResponse.INVALID_TOKEN">The password for <span class="color-primary">{{ userEmail }}</span> could not be reset. The token has expired or is invalid, please <a @click="$router.push({name: 'contact'})">contact-us</a> if the issue persist.</p>
                    <p v-else-if="resetError === PasswordResetResponse.INVALID_USER">No user matching <span class="color-primary">{{ userEmail }}</span> could be found. Please <a @click="$router.push({name: 'contact'})">contact-us</a> if the issue persist.</p>
                    <p v-else>The password for <span class="color-primary">{{ userEmail }}</span> could not be reset. Please try again later or <a @click="$router.push({name: 'contact'})">contact-us</a> if the issue persist.</p>
                    <button v-if="resetError === PasswordResetResponse.INVALID_TOKEN || resetError === PasswordResetResponse.INVALID_USER" class="button expanded ghost" @click="goToPasswordLost">Send a new reset link</button>
                    <button v-else class="button expanded ghost" @click="resetError = false">Try again</button>
                </div>
                <div v-else>
                    <p>Please enter a new password for <span class="color-primary">{{ userEmail }}</span></p>
                    <text-input
                        label="New password"
                        type="password"
                        name="new-password"
                        :required="true"
                        :value="newPassword"
                        @change="newPassword = $event"
                    ></text-input>
                    <text-input
                        label="Confirmation"
                        type="password"
                        name="confirm-password"
                        :required="true"
                        :value="newPasswordConfirmation"
                        @change="newPasswordConfirmation = $event"
                    ></text-input>
                    <p class="display-small color-battleship-gray">The password needs to be at least 8 characters</p>
                    <button class="button expanded" @click="resetPassword" :disabled="!newPassword || newPassword !== newPasswordConfirmation || resetLoading || newPassword.length < 8">Reset my password</button>
                </div>
            </div>
            <div class="cell" v-if="currentStep === LoginStep.PASSWORD_LOST">
                <p class="login-modal-step-title display-4 bold">Lost your password?</p>
                <div v-if="resetSuccess" dusk="login-reset-password-success">
                    <p class="display-large">A reset link has been sent to your address: <span class="color-primary">{{ userEmail }}</span></p>
                </div>
                <div v-else>
                    <text-input
                        label="Email"
                        type="email"
                        :value="userEmail"
                        @change="handleInput('email', $event)"
                        :error-message="(resetError) ? 'Could not find an account matching this email' : ''"
                    ></text-input>
                    <button class="button full-width" @click="sendPasswordResetLink" :disabled="resetLoading" dusk="login-reset-password">Send a reset link</button>
                </div>
            </div>
            <div class="cell" v-if="currentStep === LoginStep.NEW_USER">
                <p class="login-modal-step-title display-4 bold">Edit your details</p>
                <div class="warning-box grid-x" v-if="codeGenerationError">
                    <span class="warning-icon cell shrink" v-html="Icon.ERROR"></span>
                    <div class="cell auto">
                        <p class="display-small margin-0">{{ codeGenerationError }}</p>
                    </div>
                </div>
                <text-input
                    label="Name"
                    type="text"
                    name="fullname"
                    :value="userName"
                    @change="handleInput('name', $event)"
                    :required="true"
                ></text-input>
                <phone-input
                    :required="true"
                    name="tel"
                    :value="userPhone"
                    placeholder="Phone*"
                    :preferred-countries="['US', 'KY', 'CA', 'GB']"
                    default-country="KY"
                    mode="international"
                    @change="handleInput('phone', $event)"
                    @validate="validatePhone"
                    :error-message="phoneError"
                >
                </phone-input>
                <text-input
                    label="Password"
                    type="password"
                    name="password"
                    :value="userPassword"
                    @change="handleInput('password', $event)"
                    :required="true"
                ></text-input>
                <button class="button full-width" @click="register" :disabled="!userName || (!userPhone || phoneError) || !userPassword || isLoading" dusk="login-register-btn">Register</button>
                <div class="align-middle warning-box grid-x" v-if="registrationError">
                    <span class="warning-icon cell shrink" v-html="Icon.ERROR"></span>
                    <div class="cell auto">
                        <p class="display-small margin-0">{{ registrationError }}</p>
                    </div>
                </div>
            </div>
            <!-- <div class="cell" v-if="currentStep === LoginStep.OTP_VALIDATION">
                <p class="login-modal-step-title display-3">Enter verification code</p>
                <p>A text message with the code was sent to your phone ({{ userPhone }})</p>
                <div class="align-middle warning-box grid-x" v-if="registrationError">
                    <span class="warning-icon cell shrink" v-html="Icon.ERROR"></span>
                    <div class="cell auto">
                        <p class="display-small margin-0">{{ registrationError }}</p>
                    </div>
                </div>
                <text-input label="Verification Code" type="text" :value="otpCode" name="otpcode" @change="otpCode = $event"></text-input>
                <transition name="quick-fade">
                    <p v-if="canResendCode">Didn't receive the code? <a @click.prevent="verifyPhone">Send another one</a></p>
                </transition>
                <button class="button full-width" @click="register" :disabled="otpCode.length < 6" dusk="login-confirm-btn">
                    Confirm and Sign In
                </button>
            </div> -->
            <div class="cell" v-if="currentStep === LoginStep.EMAIL_NOT_VERIFIED">
                <p class="login-modal-step-title display-3">Verify your email</p>
                <p>To complete the process, please verify your email at {{ userEmail }}. If you can't find the email in your inbox, remember to check your spam folder as well.</p>
                <p>If you haven't received the email, please click on the following link to <a href="#" style="text-decoration: underline;" @click.prevent="resendEmailVerification">resend it.</a></p>
                <alert :type="AlertType.SUCCESS" title="Email sent" style="margin-bottom: 15px;" v-if="emailSent">
                    Activation link has been sent to your email.
                </alert>
                <button class="button full-width" @click="closeLoginModal">
                    Close
                </button>
            </div>
        </div>
    </div>
</template>
<script lang="ts">
import Vue from 'vue';
import Component from 'vue-class-component';
import SocialLoginButton from '@/components/front/user/SocialLoginButton.vue';
import UserModule from '@/stores/modules/user';
import { getModule } from 'vuex-module-decorators';
import User from '@/models/user';
import TextInput from '@/components/common/forms/TextInput.vue';
import PhoneInput from '@/components/common/forms/PhoneInput.vue';
import { LoginStep } from '@/stores/modules/user';
import CartModule from '@/stores/modules/cart';
import { Icon } from '@/front/icons';
import { AuthStatus, PasswordResetResponse } from '@/utils/types';
import Settings, { SettingNamespace } from '@/utils/settings';
import Alert from '@/components/common/ui/Alert.vue';
import { AlertType } from '@/utils/types';

@Component({
    components: { SocialLoginButton, TextInput, PhoneInput, Alert }
})
export default class UserLoginModal extends Vue
{
    userModule!: UserModule;
    cartModule!: CartModule;
    wrongPassword: boolean = false;
    resetSuccess: boolean = false;
    resetLoading: boolean = false;
    resetError: string | boolean = '';
    emailError: string = '';
    phoneError: string = '';
    newPassword: string = '';
    newPasswordConfirmation: string = '';
    canResendCode: boolean = false;
    storeName: string = '';
    isLoading: boolean = false;
    emailSent: boolean = false;

    async created() {
        this.userModule = getModule(UserModule, this.$store);
        this.cartModule = getModule(CartModule, this.$store);
        const settings = await Settings.getInstance();
        this.storeName = settings.get(SettingNamespace.GENERAL, 'store_name')?.value;
    }

    mounted() {
        this.$watch('isModalOpen', this.onModalOpen);
    }

    onModalOpen(modalOpen: boolean) {
        if (modalOpen) {
            this.cleanErrors();
        }
    }

    cleanErrors() {
        this.resetSuccess = false;
        this.resetError = '';
        this.emailError = '';
        this.phoneError = '';
        this.resetLoading = false;
        this.wrongPassword = false;
        this.userModule.setRegistrationError('');
        this.userModule.setCodeGenerationError('');
        this.userModule.setAuthStatus(null);
    }

    handleInput(name: string, value: string) {
        this.userModule.setUserTempVariable({key: name, value})
    }

    goToPasswordLost() {
        this.cleanErrors();
        this.userModule.setLoginCurrentStep(LoginStep.PASSWORD_LOST);
    }

    isEmailValid(value: string) {
        const emailRegex = /\S+@\S+\.\S+/;
        if (!emailRegex.test(value)) {
            this.emailError = 'Please enter a valid email.';
            return false;
        }
        return true;
    }

    validatePhone(phoneObject: any) {
        if (phoneObject.formatted && !phoneObject.valid)  {
            this.phoneError = 'Please enter a valid phone number.';
            return false;
        }
        this.phoneError = '';
        return true;
    }

    async resetPassword() {
        this.resetLoading = true;
        const response = await User.resetPassword(this.userModule.resetToken, this.userModule.userTempVariables.email, this.newPassword, this.newPasswordConfirmation);
        this.resetLoading = false;
        if (response && response === PasswordResetResponse.PASSWORD_RESET) {
            this.userModule.setUserTempVariable({key: 'password', value: this.newPassword});
            return this.login();
        }
        this.resetError = response;
    }

    async sendPasswordResetLink() {
        this.resetLoading = true;
        const response = await User.lostPassword(this.userModule.userTempVariables.email);
        this.resetLoading = false;
        if (response) {
            return this.resetSuccess = true;
        }
        this.resetError = 'reset-email-error';
    }

    get userPassword() {
        return this.userModule.userTempVariables.password;
    }

    get userEmail() {
        return this.userModule.userTempVariables.email;
    }

    get userName() {
        return this.userModule.userTempVariables.name;
    }

    get userPhone() {
        return this.userModule.userTempVariables.phone;
    }

    get otpCode() {
        return this.userModule.otpCode;
    }

    set otpCode(code: string) {
        this.userModule.setOtpCode(code);
    }

    get codeGenerationError() {
        return this.userModule.codeGenerationError;
    }

    get Icon() {
        return Icon;
    }

    get AlertType() {
        return AlertType;
    }

    closeLoginModal() {
        this.userModule.closeLoginModal();
    }

    async checkUserExists() {
        this.cleanErrors();
        if (this.isEmailValid(this.userEmail)) {
            const userExists = await User.exists(this.userEmail);
            if (userExists.success && (<{has_provider: boolean}>userExists.content).has_provider) {
                return this.userModule.setAuthStatus(AuthStatus.LOGIN_ERROR);
            }
            if (userExists.success) {
                return this.userModule.setLoginCurrentStep(LoginStep.USER_EXISTS);
            }
            return this.userModule.setLoginCurrentStep(LoginStep.NEW_USER);
        }
    }

    async login() {
        const user = await User.login(this.userEmail, this.userPassword);
        if (user) {
            const isUserEmailVerified = await User.emailVerified(this.userEmail, this.userPassword);
            if (isUserEmailVerified) {
                this.userModule.setLoginCurrentStep(LoginStep.LOGGED_IN);
                this.userModule.setCurrentUser(user);
                this.userModule.closeLoginModal();
                this.cartModule.setRegistrationError(false);
            }
            this.userModule.setLoginCurrentStep(LoginStep.EMAIL_NOT_VERIFIED);
        } else {
            this.wrongPassword = true;
        }
    }

    async resendEmailVerification() {
        const emailSent = await this.userModule.resendEmailVerification();
        if (emailSent) {
            this.emailSent = true;
        }
        window.setTimeout(() => {
            this.emailSent = false;
        }, 5 * 1000);
    }

    async verifyPhone() {
        this.canResendCode = false;
        window.setTimeout(() => {
            this.canResendCode = true;
        }, 60 * 1000);
        await !this.userModule.requestCode();
    }

    async register() {
        this.isLoading = true;
        await this.userModule.register();
        this.isLoading = false;
    }

    get registrationError() {
        return this.userModule.registrationError;
    }

    get isModalOpen() {
        return this.userModule.loginModalOpen;
    }

    get currentStep() {
        return this.userModule.loginCurrentStep;
    }

    get wrongPasswordText() {
        if (this.wrongPassword) return 'Wrong password';
        return '';
    }

    get authError() {
        if (this.userModule.authStatus === AuthStatus.LOGIN_ERROR) {
            return "We're sorry, your account was created using a different login method. Please sign in using the same provider (Facebook or Google) that you used to create your account.";
        }
        if (this.userModule.authStatus === AuthStatus.WRONG_PROVIDER) {
            return "This account is using a different social network authentication, please select another one.";
        }
        if (this.userModule.authStatus === AuthStatus.WRONG_PROVIDER_ID) {
            return "The social network authentication token mismatches, please contact us for further assistance.";
        }
        if (this.userModule.authStatus === AuthStatus.NO_EMAIL) {
            return "The social network authentication doesn't have an email associated, please contact us for further assistance.";
        }
        return '';
    }

    get LoginStep() {
        return LoginStep;
    }

    get PasswordResetResponse() {
        return PasswordResetResponse;
    }
}
</script>
<style lang="scss" scoped>
@import '~foundation-sites/scss/foundation';
@import '~scss/front/settings';

.login-modal-container {
    @include breakpoint(medium down) {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 60px;
        padding: 30px;
        overflow-y: auto;
        background: white;
        z-index: 12;
    }
    @include breakpoint(large) {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 100;
        background: rgba(255,255,255,.5);
        backdrop-filter: blur(5px);
    }
}

.login-modal {
    @include breakpoint(large) {
        width: 450px;
        padding: 50px;
        position: relative;
        background: white;
        border: 1px solid #E0E0E0;
        box-shadow: 5px 5px 40px 0 rgba(0,0,0,0.09);
        border-radius: var(--global-radius);
    }
}

.login-modal-step-title {
    margin-bottom: 30px;
}

.login-modal-close-button {
    position: absolute;
    top: 20px;
    right: 20px;
    width: 20px;

}

</style>
